import React, { useState } from 'react';
import { Link } from 'react-router-dom'; 
// Diseño «Dividida» (8-oct-2026): la mitad azul, la fuente y los estilos de campo viven en AuthMarca (compartidos con el login).
import { PanelMarca, FUENTE, INPUT, LABEL } from '../components/AuthMarca';
import { useAuth } from '../../../context/AuthContext';
import { useError } from '../../../context/ErrorContext';
import { BaseApiError } from '../../../api/baseClient';

// Atribución GA4: lee el client_id de gtag para que el evento `purchase` (que dispara
// el webhook de MercadoPago server-side) se una a la sesión/origen real del usuario.
const GA4_MEASUREMENT_ID = 'G-R8BV4P5BDS';
const getGa = (campo: 'client_id' | 'session_id'): Promise<string | null> =>
  new Promise((resolve) => {
    try {
      const g = (window as any).gtag;
      if (typeof g !== 'function') return resolve(null);
      let done = false;
      const finish = (v: string | null) => { if (!done) { done = true; resolve(v); } };
      g('get', GA4_MEASUREMENT_ID, campo, (id: string | number) => finish(id ? String(id) : null));
      setTimeout(() => finish(null), 1200); // no bloquear el registro si gtag tarda
    } catch { resolve(null); }
  });
// Atribución del alta (4-oct-2026): lealify.com manda en la URL de /register el botón
// (origen), la página del botón (pagina), el sitio externo del que llegó la persona (ref) y su
// página de entrada (lp), además de los utm_*. Se guardan en sessionStorage al cargar para no
// perderlos si navega (p.ej. entra a «Iniciar sesión» y vuelve). El servidor los sanea y los
// guarda en tenant_settings.acquisition.
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
// `referido` (12-oct-2026): código del referidor que trae lealify.com/r/<code> (≠ `ref`, el sitio externo de origen).
const LANDING_KEYS = ['origen', 'pagina', 'ref', 'lp', 'referido'];
const ATTR_STORAGE = 'lf_signup_attr';
const pick = (p: URLSearchParams, keys: string[]): Record<string, string> => {
  const out: Record<string, string> = {};
  keys.forEach((k) => { const v = p.get(k); if (v) out[k] = v.slice(0, 120); });
  return out;
};
const readAttr = (): { utm: Record<string, string>; landing: Record<string, string> } => {
  const fromUrl = (() => {
    try {
      const p = new URLSearchParams(window.location.search);
      return { utm: pick(p, UTM_KEYS), landing: pick(p, LANDING_KEYS) };
    } catch { return { utm: {}, landing: {} }; }
  })();
  if (Object.keys(fromUrl.utm).length || Object.keys(fromUrl.landing).length) {
    try { sessionStorage.setItem(ATTR_STORAGE, JSON.stringify(fromUrl)); } catch { /* modo privado */ }
    return fromUrl;
  }
  try {
    const saved = JSON.parse(sessionStorage.getItem(ATTR_STORAGE) || 'null');
    if (saved && typeof saved === 'object') return { utm: saved.utm || {}, landing: saved.landing || {} };
  } catch { /* */ }
  return fromUrl;
};
readAttr(); // al cargar la página: guarda lo que traiga la URL antes de que se pierda
const getUtmParams = (): Record<string, string> | null => {
  const { utm } = readAttr();
  return Object.keys(utm).length ? utm : null;
};
const getLandingAttr = (): Record<string, string> | null => {
  const { landing } = readAttr();
  return Object.keys(landing).length ? landing : null;
};


// Demo por WhatsApp para quien prefiere que se lo muestren (≈37 % abre el registro y no termina, GA4 24-sep→7-oct).
// La frase contiene «programa de lealtad para mi negocio» → la toma el bot de ventas (ORIGENES de lealtad).
const WA_DEMO = `https://wa.me/526861326204?text=${encodeURIComponent(
  'Hola, estaba creando mi cuenta en Lealify y prefiero que me muestren el programa de lealtad para mi negocio.')}`;

const Register: React.FC = () => { 
  // --- Tu Lógica de Estado y Hooks (sin cambios) ---
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [countryCode, setCountryCode] = useState('+52');
  const [phoneNumber, setPhoneNumber] = useState('');
  const { register, loading } = useAuth();
  const { handleFirebaseError, clearErrors, addValidationError } = useError();
  // -----------------------------------------------

  // Validación Nivel 1: formato + anti-basura (sin backend)
  const isValidPhone = (_cc: string, num: string) => {
    const d = num.replace(/\D/g, '');
    if (d.length !== 10) return false;          // MX y USA: 10 dígitos
    if (/^[01]/.test(d)) return false;          // ni lada MX ni área USA empiezan con 0/1
    if (/^(\d)\1{9}$/.test(d)) return false;    // todos iguales (1111111111)
    if ('0123456789'.includes(d) || '9876543210'.includes(d)) return false; // secuencial
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearErrors();
    if (!email || !password || !confirmPassword || !displayName || !businessName) {
      addValidationError('Por favor complete todos los campos', 'register-form'); return;
    }
    if (!isValidPhone(countryCode, phoneNumber)) {
      addValidationError('Ingresa un número de WhatsApp válido (10 dígitos, sin el 01)', 'phoneNumber'); return;
    }
    if (password.length < 8) {
      addValidationError('La contraseña debe tener al menos 8 caracteres', 'password'); return;
    }
    if (password !== confirmPassword) {
      addValidationError('Las contraseñas no coinciden', 'password-match'); return;
    }
    try {
      // session_id (18-oct-2026): los eventos del servidor (verificó, terminó el asistente, pagó) caen en la visita del alta.
      const [gaClientId, gaSessionId] = await Promise.all([getGa('client_id'), getGa('session_id')]);
      const utm = getUtmParams();
      const landing = getLandingAttr();
      await register(email, password, displayName, businessName.trim(), phoneNumber.trim(), countryCode.trim(), { gaClientId, gaSessionId, utm, landing });
    } catch (err) {
      // El servidor manda el motivo exacto («Este correo electrónico ya está registrado», contraseña
      // corta…); handleFirebaseError solo traduce códigos de Firebase y lo tapaba con «Ha ocurrido un
      // error inesperado» (24-sep: 8 intentos en una hora y se fue sin cuenta).
      if (err instanceof BaseApiError && err.message) addValidationError(err.message, 'register-form');
      else handleFirebaseError(err, 'register');
    }
  };
  // ---------------------------------------------

  // flex-shrink-0: #root mide 100vh y es flex; sin esto, en celular encoge la franja azul a 60 px.
  return (
    <div className="force-light min-h-screen flex-shrink-0 flex flex-col md:flex-row bg-white" style={FUENTE}>

      <PanelMarca titulo="Tus clientes vuelven más seguido." />


      {/* Mitad blanca: el formulario (mismos campos, mismo orden y misma lógica que antes) */}
      <main className="flex flex-1 items-center justify-center px-5 py-8 md:px-12 md:py-12">
        <div className="w-full max-w-[440px]">
          <h2 style={FUENTE} className="mb-7 text-center text-[28px] md:text-[34px] font-extrabold tracking-tight text-[#0E1E3A]">Crea tu cuenta gratis</h2>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="displayName" className={LABEL}>Tu nombre</label>
              <input id="displayName" name="displayName" type="text" autoComplete="name" required value={displayName} onChange={(e) => setDisplayName(e.target.value)} className={INPUT} placeholder="Tu nombre completo" />
            </div>
            <div>
              <label htmlFor="businessName" className={LABEL}>Nombre de tu negocio</label>
              <input id="businessName" name="businessName" type="text" autoComplete="organization" required value={businessName} onChange={(e) => setBusinessName(e.target.value)} className={INPUT} placeholder="Ej. Cafetería La Esquinita" />
            </div>
            <div>
              <label htmlFor="phoneNumber" className={LABEL}>WhatsApp</label>
              <div className="flex h-12 rounded-xl border border-slate-300 bg-white transition focus-within:border-[#0057B8] focus-within:ring-4 focus-within:ring-[#0057B8]/15">
                <select aria-label="País" value={countryCode} onChange={(e) => setCountryCode(e.target.value)} className="rounded-l-xl border-0 border-r border-slate-200 bg-slate-50 pl-3 pr-8 text-[15px] focus:ring-0 text-[#0E1E3A] outline-none">
                  <option value="+52">🇲🇽 +52</option>
                  <option value="+1">🇺🇸 +1</option>
                </select>
                <input id="phoneNumber" name="phoneNumber" type="tel" inputMode="numeric" autoComplete="tel-national" required value={phoneNumber} onChange={(e) => { const v = e.target.value.replace(/\D/g, ''); if (v.length <= 10) setPhoneNumber(v); }} className="min-w-0 flex-1 rounded-r-xl border-0 bg-transparent px-4 focus:ring-0 text-[15px] text-[#0E1E3A] placeholder-slate-400 outline-none" placeholder="10 dígitos" />
              </div>
              <p className="mt-1.5 text-xs text-slate-500">Te mandamos tips de arranque y soporte por aquí. Nada de spam.</p>
            </div>
            <div>
              <label htmlFor="email" className={LABEL}>Correo electrónico</label>
              <input id="email" name="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={INPUT} placeholder="correo@ejemplo.com" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2 sm:gap-3">
              <div>
                <label htmlFor="password" className={LABEL}>Contraseña</label>
                <input id="password" name="password" type="password" autoComplete="new-password" required value={password} onChange={(e) => setPassword(e.target.value)} className={INPUT} placeholder="Mínimo 8 caracteres" />
              </div>
              <div>
                <label htmlFor="confirmPassword" className={LABEL}>Confirmar contraseña</label>
                <input id="confirmPassword" name="confirmPassword" type="password" autoComplete="new-password" required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className={INPUT} placeholder="••••••••" />
              </div>
            </div>
            <div className="pt-2">
              <button type="submit" disabled={loading}
                className={`h-12 w-full rounded-full bg-[#0057B8] text-[15px] font-bold text-white shadow-lg shadow-[#0057B8]/25 transition hover:bg-[#004494] focus:outline-none focus:ring-4 focus:ring-[#0057B8]/30 active:scale-[0.99] ${loading ? 'cursor-not-allowed opacity-70' : ''}`}>
                {loading ? 'Registrando...' : 'Crear cuenta gratis'}
              </button>
            </div>
          </form>
          <p className="mt-5 text-center text-sm text-slate-600">
            ¿Ya tienes una cuenta?{' '}
            <Link to="/login" className="font-bold text-[#0057B8] hover:underline">Inicia sesión</Link>
          </p>

          {/* Quien prefiere que se lo muestren: demo por WhatsApp. Debajo del formulario, nunca encima (no robarle altas). */}
          <div className="mt-6 border-t border-slate-200 pt-6 text-center">
            <p className="mb-3 text-sm text-slate-600">¿Prefieres que te lo mostremos?</p>
            <a href={WA_DEMO} target="_blank" rel="noopener noreferrer"
              onClick={() => { try { (window as any).gtag?.('event', 'contacto_whatsapp', { origen: 'registro', pagina: 'register' }); } catch { /* nunca bloquear el clic */ } }}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#25D366] text-[15px] font-bold text-white shadow-lg shadow-[#25D366]/30 transition hover:bg-[#1ebe5b]">
              <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5 fill-current">
                <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.64.07-.3-.15-1.25-.46-2.38-1.47-.88-.79-1.47-1.76-1.65-2.06-.17-.3-.02-.46.13-.6.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.6-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.21 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.22 1.36.19 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35M12.05 21.79h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37A9.86 9.86 0 0 1 2.16 11.9C2.17 6.45 6.6 2.02 12.06 2.02c2.64 0 5.12 1.03 6.99 2.9a9.82 9.82 0 0 1 2.89 6.99c0 5.45-4.43 9.88-9.89 9.88m8.41-18.3A11.81 11.81 0 0 0 12.05 0C5.5 0 .16 5.34.16 11.89c0 2.1.55 4.14 1.59 5.95L.06 24l6.3-1.65a11.88 11.88 0 0 0 5.68 1.45h.01c6.55 0 11.89-5.34 11.89-11.9 0-3.17-1.24-6.16-3.48-8.41" />
              </svg>
              Solicita tu demo por WhatsApp
            </a>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Register;
